"use client";

import { X } from "lucide-react";
import {
  AnimatePresence,
  motion,
  useIsPresent,
  useReducedMotion,
} from "motion/react";
import {
  cloneElement,
  createContext,
  isValidElement,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { EASE_OUT, SPRING_PANEL } from "../../lib/ease";
import { cn } from "../../lib/utils";

type ModalContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerId: string;
  contentId: string;
};

const ModalContext =
  createContext<ModalContextValue | null>(null);

function useModalContext(component: string) {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error(`${component} must be used within <Modal>`);
  }
  return context;
}

export interface ModalProps {
  children: ReactNode;
  /** Controlled open state. */
  open?: boolean;
  /** Initial state when used uncontrolled. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * A modal whose full-size surface unfolds outward from its exact center.
 * Supports controlled and uncontrolled state through composable primitives.
 */
export function Modal({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
}: ModalProps) {
  const id = useId();
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!controlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange],
  );

  const value = useMemo<ModalContextValue>(
    () => ({
      open,
      setOpen,
      triggerId: `${id}-trigger`,
      contentId: `${id}-content`,
    }),
    [id, open, setOpen],
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
}

export interface ModalTriggerProps {
  children: ReactElement;
}

/** Wraps one interactive element and opens or closes the modal. */
export function ModalTrigger({
  children,
}: ModalTriggerProps) {
  const context = useModalContext("ModalTrigger");
  if (!isValidElement(children)) return children;

  const child = children as ReactElement<Record<string, unknown>>;
  const childOnClick = child.props.onClick as
    | ((event: React.MouseEvent<HTMLElement>) => void)
    | undefined;

  return cloneElement(child, {
    id: context.triggerId,
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      childOnClick?.(event);
      if (!event.defaultPrevented) context.setOpen(!context.open);
    },
    "aria-haspopup": "dialog",
    "aria-expanded": context.open,
    "aria-controls": context.open ? context.contentId : undefined,
  });
}

export interface ModalCloseProps {
  children: ReactElement;
}

/** Wraps one interactive element and closes the modal. */
export function ModalClose({
  children,
}: ModalCloseProps) {
  const context = useModalContext("ModalClose");
  if (!isValidElement(children)) return children;

  const child = children as ReactElement<Record<string, unknown>>;
  const childOnClick = child.props.onClick as
    | ((event: React.MouseEvent<HTMLElement>) => void)
    | undefined;

  return cloneElement(child, {
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      childOnClick?.(event);
      if (!event.defaultPrevented) context.setOpen(false);
    },
  });
}

export interface ModalHeaderProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Heading for the modal, rendered as its `h2`. */
  title?: ReactNode;
  /** Supporting line beneath the title. */
  description?: ReactNode;
  /** Controls between the title block and the close, such as a view toggle. */
  actions?: ReactNode;
  /** Drops the close control, for a modal dismissed some other way. */
  showClose?: boolean;
  closeLabel?: string;
  /** Rule beneath the header, for when the body scrolls under it. */
  bordered?: boolean;
}

/**
 * Header row for a modal: title, optional description and actions, and the
 * close control.
 *
 * The close lives here rather than floating over the panel so it takes part in
 * layout. An absolutely positioned control cannot be laid out against, which
 * left every consumer either reserving space by hand or getting away with a
 * title short enough not to collide. The header renders even with no title,
 * because the close still needs a row of its own to sit in.
 */
export function ModalHeader({
  title,
  description,
  actions,
  showClose = true,
  closeLabel = "Close modal",
  bordered = false,
  className,
  children,
  ...rest
}: ModalHeaderProps) {
  const context = useModalContext("ModalHeader");
  const reduce = useReducedMotion();

  return (
    <div
      data-slot="modal-header"
      className={cn(
        "flex items-start gap-3 px-6 py-4",
        bordered && "border-b border-v2-border",
        className,
      )}
      {...rest}
    >
      <div className="min-w-0 flex-1">
        {title ? (
          <h2 className="text-v2-base font-semibold leading-snug text-v2-foreground">
            {title}
          </h2>
        ) : null}
        {description ? (
          <p className="mt-1 text-v2-sm text-v2-muted-foreground">{description}</p>
        ) : null}
        {children}
      </div>

      {actions ? (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      ) : null}

      {showClose ? (
        <motion.button
          type="button"
          aria-label={closeLabel}
          data-modal-close=""
          onClick={() => context.setOpen(false)}
          initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            delay: reduce ? 0 : 0.16,
            duration: reduce ? 0.12 : 0.2,
            ease: EASE_OUT,
          }}
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-v2-md bg-v2-foreground/[0.05] text-v2-muted-foreground transition-colors hover:bg-v2-foreground/[0.08] hover:text-v2-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-ring"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </motion.button>
      ) : null}
    </div>
  );
}

export interface ModalContentProps {
  children: ReactNode;
  /** Accessible name announced by screen readers. */
  ariaLabel: string;
  /** Optional id of descriptive content inside the modal. */
  ariaDescribedBy?: string;
  /** Close on Escape or backdrop press. Default true. */
  dismissible?: boolean;
  className?: string;
  backdropClassName?: string;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * The modal enters the way every other v2 overlay does: opacity plus a small
 * scale, on the shared panel spring.
 *
 * It used to unfold via `clip-path: inset(48% …)` → `inset(0% …)`, which made it
 * the only surface in the set that morphed while popover, tooltip, toast and
 * bottom sheet all faded. That inconsistency was visible whenever two overlays
 * appeared in the same flow — a row menu opening a rename dialog, for instance.
 */
const PANEL_ENTER = { opacity: 1, scale: 1 } as const;
const PANEL_INITIAL = { opacity: 0, scale: 0.96 } as const;

/**
 * Both layers leave together, on one short tween.
 *
 * The panel used to exit on `SPRING_PANEL`, which is overdamped and so has no
 * fixed duration. Measured against the backdrop's 0.28s tween, the backdrop
 * reached zero at 320ms while the panel was still at 9% opacity and took a
 * further 237ms to go — so the blur came off the page underneath a panel that
 * was still on screen. A spring is right for an entrance, where the panel is
 * arriving at a resting size; there is nothing to settle into on the way out.
 */
const EXIT_TRANSITION = { duration: 0.16, ease: EASE_OUT } as const;
const PANEL_EXIT = { opacity: 0, scale: 0.97, transition: EXIT_TRANSITION } as const;

/**
 * Holds the page still for as long as the overlay is on screen.
 *
 * Mounted inside `AnimatePresence` so the lock lifts when the overlay is
 * actually removed rather than when `open` flips. Those were 480ms apart
 * before the exits were brought onto one tween, and restoring the page's
 * scrollbar under a modal that is still fading shifts everything behind it by
 * the scrollbar's width.
 */
function ScrollLock() {
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  return null;
}

function getFocusableElements(root: HTMLElement | null) {
  if (!root) return [];
  return Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((element) => element.tabIndex >= 0);
}

function PresencePointerGate({
  children,
}: {
  children: (isPresent: boolean) => ReactNode;
}) {
  return children(useIsPresent());
}

export function ModalContent({
  children,
  ariaLabel,
  ariaDescribedBy,
  dismissible = true,
  className,
  backdropClassName,
}: ModalContentProps) {
  const context = useModalContext("ModalContent");
  const reduce = useReducedMotion() ?? false;
  // Already mounted in a browser. The gate is there for the server, where
  // nothing can be portalled into a document that does not exist — but
  // starting at `false` in the browser too meant a remount while open blanked
  // the modal for one render and then replayed its enter animation from
  // `initial`, which reads as the modal flashing back into view on its way out.
  const [mounted, setMounted] = useState(() => typeof document !== "undefined");
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  // Read through a ref so the effect below can depend on `open` alone. Callers
  // pass an inline `onOpenChange` almost everywhere, which hands `setOpen` a
  // new identity on every parent render.
  const setOpenRef = useRef(context.setOpen);
  useEffect(() => {
    setOpenRef.current = context.setOpen;
  }, [context.setOpen]);

  // Keyed on `open` rather than the whole context: an unstable context tore
  // this down and re-ran it on every parent render, which cancelled and
  // re-scheduled the focus grab each time.
  useEffect(() => {
    if (!context.open) return;

    const focusFrame = requestAnimationFrame(() => {
      // The header's close control is the first focusable element in the panel,
      // but it is the least useful place to land: a modal that asks for input
      // should open on that input. Prefer anything else, and fall back to the
      // close (then the panel) only when there is nothing else to focus.
      const focusable = getFocusableElements(overlayRef.current);
      const preferred = focusable.find(
        (element) => !element.hasAttribute("data-modal-close"),
      );
      (preferred ?? focusable[0] ?? panelRef.current)?.focus();
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && dismissible) {
        event.preventDefault();
        setOpenRef.current(false);
        return;
      }

      if (event.key !== "Tab") return;
      const focusable = getFocusableElements(overlayRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        panelRef.current?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [context.open, dismissible]);

  // Focus goes back to the trigger only when the modal was being driven from
  // the keyboard.
  //
  // Returning it unconditionally lit the trigger's focus ring and popped its
  // tooltip a beat after the panel had gone, so a button the reader had
  // clicked appeared to do something on its own. Never returning it is the
  // other failure: a keyboard reader who closes a dialog is left at the top of
  // the document with nothing to say where they were (WCAG 2.4.3), and this is
  // the primitive behind every modal in the app.
  //
  // So the modality of the last interaction decides. A pointer closed it —
  // nothing moves. A key closed it — focus goes home.
  useEffect(() => {
    if (!context.open) return;
    const invoker = document.activeElement as HTMLElement | null;
    let byKeyboard = false;
    const onKey = () => {
      byKeyboard = true;
    };
    const onPointer = () => {
      byKeyboard = false;
    };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("pointerdown", onPointer, true);

    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pointerdown", onPointer, true);
      if (!byKeyboard) return;
      (document.getElementById(context.triggerId) ?? invoker)?.focus();
    };
  }, [context.open, context.triggerId]);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {context.open ? (
        <PresencePointerGate>
          {(isPresent) => (
            <div
              ref={overlayRef}
              className="pointer-events-none fixed inset-0 z-[100]"
            >
              <ScrollLock />
          <motion.button
            type="button"
            aria-label="Dismiss modal"
            tabIndex={-1}
            disabled={!dismissible}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: EXIT_TRANSITION }}
            style={{ pointerEvents: isPresent ? "auto" : "none" }}
            transition={{
              duration: reduce ? 0.1 : 0.28,
              ease: EASE_OUT,
            }}
            onClick={() => context.setOpen(false)}
            className={cn(
              "pointer-events-auto absolute inset-0 h-full w-full cursor-default bg-v2-background/10 backdrop-blur-sm",
              backdropClassName,
            )}
          />

          <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-y-auto p-4 drop-shadow-2xl">
            {/* Drop-shadow reads the clipped child's alpha, so depth follows the
                unfolding silhouette without introducing another panel layer. */}
            <div className="flex w-full flex-col items-center py-8">
              <motion.div
                ref={panelRef}
                id={context.contentId}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
                aria-describedby={ariaDescribedBy}
                tabIndex={-1}
                initial={reduce ? { opacity: 0 } : PANEL_INITIAL}
                animate={reduce ? { opacity: 1 } : PANEL_ENTER}
                exit={reduce ? { opacity: 0, transition: EXIT_TRANSITION } : PANEL_EXIT}
                style={{ pointerEvents: isPresent ? "auto" : "none" }}
                transition={reduce ? { duration: 0.14, ease: EASE_OUT } : SPRING_PANEL}
                className={cn(
                  "pointer-events-auto relative w-full max-w-[26rem] origin-center overflow-hidden rounded-v2-xl border border-v2-border bg-v2-background will-change-[opacity,transform]",
                  className,
                )}
              >
                {children}
              </motion.div>
            </div>
          </div>
            </div>
          )}
        </PresencePointerGate>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
